import { useEffect, useState } from 'react';
import { Eye, EyeOff, Loader } from 'lucide-react';

import { eventTeamApiService } from '../../services/eventTeamApi';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../utils/cn';
import type { EventWatcher } from '../../types/eventTeam';

export interface EventWatcherButtonProps {
  eventId: string;
  /** Bumped by parent on relevant WS events. */
  refreshNonce?: number;
  /** Optional callback so the parent can update an aggregate watcher count. */
  onWatchersChanged?: (watchers: EventWatcher[]) => void;
}

function watcherUuid(w: EventWatcher): string | undefined {
  return w.watcher_uuid ?? w.user_uuid;
}

/**
 * Toggle button for self-watching an event. Watchers receive hourly email
 * digests on changes (guide §1.4).
 */
export const EventWatcherButton = ({
  eventId,
  refreshNonce = 0,
  onWatchersChanged,
}: EventWatcherButtonProps) => {
  const { identity } = useAuth();
  const myUuid = identity?.raw?.custom?.minecraft_uuid;
  const myEmail = identity?.email;

  const [watchers, setWatchers] = useState<EventWatcher[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const isWatching = !!myUuid && (watchers ?? []).some((w) => watcherUuid(w) === myUuid);
  const count = watchers?.length ?? 0;

  const load = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const list = await eventTeamApiService.listEventWatchers(eventId);
      setWatchers(list);
      onWatchersChanged?.(list);
    } catch {
      setWatchers(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce]);

  const toggle = async () => {
    if (!myUuid) return;
    setBusy(true);
    try {
      if (isWatching) {
        await eventTeamApiService.unwatchEvent(eventId);
      } else {
        await eventTeamApiService.watchEvent(eventId);
      }
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to toggle watch');
    } finally {
      setBusy(false);
    }
  };

  if (!myUuid) {
    return (
      <span className="glass-button-secondary !cursor-default" title="Sign-in required to watch">
        <Eye className="w-4 h-4 mr-1.5" />
        {loading ? '…' : count}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void toggle()}
      disabled={busy || loading || !myEmail}
      title={
        myEmail
          ? isWatching
            ? 'Stop hourly digest emails'
            : 'Email digest of activity, sent hourly'
          : 'No email on your account'
      }
      className={cn(
        'inline-flex items-center transition-colors',
        isWatching
          ? 'glass-button-secondary !text-green-300 !border-green-500/30 !bg-green-500/15'
          : 'glass-button-secondary',
        (busy || loading || !myEmail) && 'opacity-60 cursor-not-allowed',
      )}
    >
      {busy || loading ? (
        <Loader className="w-4 h-4 mr-1.5 animate-spin" />
      ) : isWatching ? (
        <Eye className="w-4 h-4 mr-1.5" />
      ) : (
        <EyeOff className="w-4 h-4 mr-1.5" />
      )}
      <span className="text-sm">{isWatching ? 'Watching' : 'Watch'}</span>
      <span className="ml-1.5 text-xs text-glass-400">{count}</span>
    </button>
  );
};
